import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SolarIcon from '../icons/SolarIcon';
import AuthService from '../services/auth';
import AppConfig from '../services/app-config';
import { orgLogoUrl } from './OrgLogo';

const BRAND = '#031444';
const GREEN = '#238048';
const WHITE = '#fff';

const G_PATH =
  'm123.31,170.05c0,1.55-1.1,2.87-2.61,3.2-4.76,1.04-9.65,1.57-14.62,1.57-18.98,0-37.7-7.98-50.06-21.35-10.54-11.4-15.71-25.74-14.56-40.36,1.45-18.35,10.53-28.05,12.91-30.29l49.47-49.47c1.24-1.24,3.24-1.24,4.48,0l52.77,52.77c1.83,1.83.53,4.95-2.05,4.95h-24.72c-.84,0-1.65-.33-2.24-.93l-23.97-23.98c-1.12-1.12-2.94-1.12-4.06,0l-33.87,33.86c-.89.97-4.71,5.63-5.44,14.92-.81,10.33,4.14,18.04,8.43,22.68,8.02,8.68,20.32,13.85,32.91,13.85,4.73,0,9.36-.71,13.75-2.11,1.72-.55,3.48.69,3.48,2.49v18.2Z';
const ACCENT_A =
  'm158.75,174.82h-17.81c-1.59,0-2.88-1.29-2.88-2.88v-38.65c0-1.32-1.07-2.39-2.39-2.39h-31.86c-1.35,0-2.44-1.09-2.44-2.44v-18.17c0-1.51,1.23-2.74,2.74-2.74h54.79c1.38,0,2.51,1.12,2.51,2.51v62.11c0,1.47-1.19,2.65-2.65,2.65Z';
const ACCENT_B =
  'm58.44,70.2v-32.41c0-1.37,1.11-2.49,2.49-2.49h18.37c1.37,0,2.49,1.11,2.49,2.49v9.48';

const FULL_TEXT = 'MiGarita.Online';

// Debug slow-mode: navigate to /?intro=slow to stretch all timing 4x for inspection.
const SLOW = (() => {
  try {
    return new URLSearchParams(window.location.search).get('intro') === 'slow';
  } catch { return false; }
})();
const S = SLOW ? 4 : 1; // time scale factor

// Timing (seconds)
const T_G_DUR = 1.6 * S;        // G rolls in duration
const T_CIRCLE = 1.6 * S;       // circle forms (right as G stops)
const T_ACCENT_A = 1.0 * S;     // accent A appears (earlier, during roll-in)
const T_ACCENT_B = 1.15 * S;    // accent B appears (slight stagger after A)
const T_TYPE_START = 1.2 * S;   // typewriter start (0.4s earlier)
const TYPE_CHAR_MS = 55 * S;    // ms per character
const T_HOLD_AFTER_TYPE = 0.6 * S; // hold after full text
const T_FADE_DUR = 0.6 * S;     // overlay fade out
const T_SAFETY = 6.0 * S;       // safety net

const prefersReducedMotion = () => {
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch {
    return false;
  }
};

export default function IntroAnimation({ onComplete }) {
  const [fadingOut, setFadingOut] = useState(false);
  const [typed, setTyped] = useState('');
  const [reduced] = useState(prefersReducedMotion);

  const orgKey = (() => {
    if (!AuthService.isLogged()) return null;
    return AuthService.getCurrentUserOrgKey() || AuthService.getCurrentUserOrg() || null;
  })();

  const [logoSrc, setLogoSrc] = useState(null);
  const [logoError, setLogoError] = useState(false);

  const orgDisplayName = (() => {
    if (!AuthService.isLogged()) return '';
    const friendly = AppConfig.get('friendly_name');
    if (typeof friendly === 'string' && friendly.length > 0) {
      return friendly;
    }
    const org = AuthService.getCurrentUserOrg();
    return org && org !== 'Inicie Sesion' ? org : '';
  })();
  const showOrgPin = AuthService.isLogged() && Boolean(orgDisplayName);

  // Safety net: ensure onComplete always fires.
  useEffect(() => {
    const totalMs = reduced
      ? 800 + T_FADE_DUR * 1000 + 200
      : T_SAFETY * 1000;
    const id = setTimeout(() => {
      onComplete();
    }, totalMs);
    return () => clearTimeout(id);
  }, [onComplete, reduced]);

  // Reduced motion: show final static state, hold, fade out, complete.
  useEffect(() => {
    if (!reduced) return;
    setTyped(FULL_TEXT);
    const t = setTimeout(() => setFadingOut(true), 800);
    const t2 = setTimeout(() => onComplete(), 800 + T_FADE_DUR * 1000);
    return () => {
      clearTimeout(t);
      clearTimeout(t2);
    };
  }, [reduced, onComplete]);

  // Typewriter effect, chained with framer-motion timing.
  useEffect(() => {
    if (reduced) return;
    let charIndex = 0;
    let typeInterval = null;
    let fadeStartTimer = null;
    let completeTimer = null;

    const startDelay = T_TYPE_START * 1000;

    const startTyping = setTimeout(() => {
      typeInterval = setInterval(() => {
        charIndex += 1;
        setTyped(FULL_TEXT.slice(0, charIndex));
        if (charIndex >= FULL_TEXT.length) {
          clearInterval(typeInterval);
          typeInterval = null;
          // Hold, then fade out, then complete.
          fadeStartTimer = setTimeout(() => {
            setFadingOut(true);
          }, T_HOLD_AFTER_TYPE * 1000);
          completeTimer = setTimeout(() => {
            onComplete();
          }, T_HOLD_AFTER_TYPE * 1000 + T_FADE_DUR * 1000);
        }
      }, TYPE_CHAR_MS);
    }, startDelay);

    return () => {
      clearTimeout(startTyping);
      if (typeInterval) clearInterval(typeInterval);
      if (fadeStartTimer) clearTimeout(fadeStartTimer);
      if (completeTimer) clearTimeout(completeTimer);
    };
  }, [reduced, onComplete]);

  // Load org logo (dark variant for the dark intro background).
  useEffect(() => {
    if (!showOrgPin || !orgKey) return;
    const urls = [
      orgLogoUrl(orgKey, 'dark'),
      orgLogoUrl(orgKey, 'original'),
    ];
    let cancelled = false;
    let idx = 0;
    const tryNext = () => {
      if (cancelled || idx >= urls.length) { setLogoError(true); return; }
      const url = urls[idx++];
      const img = new Image();
      img.onload = () => { if (!cancelled) setLogoSrc(url); };
      img.onerror = () => tryNext();
      img.src = url;
    };
    tryNext();
    return () => { cancelled = true; };
  }, [showOrgPin, orgKey]);

  const handleFadeComplete = () => {
    if (fadingOut) onComplete();
  };

  const rollIn = reduced
    ? { initial: false, animate: { x: 0, rotate: 0 } }
    : {
        initial: { x: -(typeof window !== 'undefined' ? window.innerWidth : 800) - 200, rotate: -720 },
        animate: { x: 0, rotate: 0 },
        transition: { type: 'spring', stiffness: 60, damping: 12, duration: T_G_DUR },
      };

  const circleAnim = reduced
    ? { initial: false, animate: { scale: 1, opacity: 1 } }
    : {
        initial: { scale: 0, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        transition: { delay: T_CIRCLE, type: 'tween', ease: 'easeOut', duration: 0.8 },
      };

  const accentAAnim = reduced
    ? { initial: false, animate: { opacity: 1, scale: 1 } }
    : {
        initial: { opacity: 0, scale: 0.6 },
        animate: { opacity: 1, scale: 1 },
        transition: { delay: T_ACCENT_A, type: 'spring', stiffness: 200, damping: 14 },
      };

  const accentBAnim = reduced
    ? { initial: false, animate: { opacity: 1, scale: 1 } }
    : {
        initial: { opacity: 0, scale: 0.6 },
        animate: { opacity: 1, scale: 1 },
        transition: { delay: T_ACCENT_B, type: 'spring', stiffness: 200, damping: 14 },
      };

  const orgPinAnim = reduced
    ? { initial: false, animate: { opacity: 1, y: 0 } }
    : {
        initial: { opacity: 0, y: 8 },
        animate: { opacity: 1, y: 0 },
        transition: { delay: T_TYPE_START, type: 'tween', ease: 'easeOut', duration: 0.4 },
      };

  return (
    <AnimatePresence>
      <motion.div
        key="intro-overlay"
        initial={{ opacity: 1 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: T_FADE_DUR, ease: 'easeInOut' }}
        onAnimationComplete={handleFadeComplete}
        style={{
          position: 'fixed',
          inset: 0,
          background: BRAND,
          zIndex: 9999,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: fadingOut ? 0 : 1,
          transition: `opacity ${T_FADE_DUR}s ease-in-out`,
        }}
      >
        <motion.svg
          viewBox="0 0 211.24 211.24"
          width="220"
          height="220"
          style={{ overflow: 'visible' }}
        >
          {/* Dark blue circle (logo background) */}
          <motion.circle
            cx="105.62"
            cy="105.62"
            r="105.62"
            fill={BRAND}
            initial={circleAnim.initial}
            animate={circleAnim.animate}
            transition={circleAnim.transition}
            style={{ transformOrigin: '105.62px 105.62px' }}
          />

          {/* White G character — rolls in from left */}
          <motion.path
            d={G_PATH}
            fill={WHITE}
            initial={rollIn.initial}
            animate={rollIn.animate}
            transition={rollIn.transition}
            style={{ transformOrigin: '105.62px 105.62px' }}
          />

          {/* Green accent A (on top of G) */}
          <motion.path
            d={ACCENT_A}
            fill={GREEN}
            initial={accentAAnim.initial}
            animate={accentAAnim.animate}
            transition={accentAAnim.transition}
            style={{ transformOrigin: '105.62px 105.62px' }}
          />

          {/* Green accent B (on top of G) */}
          <motion.path
            d={ACCENT_B}
            fill={GREEN}
            initial={accentBAnim.initial}
            animate={accentBAnim.animate}
            transition={accentBAnim.transition}
            style={{ transformOrigin: '105.62px 105.62px' }}
          />
        </motion.svg>

        <div
          style={{
            marginTop: 8,
            color: WHITE,
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: '1px',
            fontFamily: '"Nexa", "Roboto", "Helvetica", "Arial", sans-serif',
            height: '1.2em',
            lineHeight: '1.2em',
            whiteSpace: 'pre',
          }}
        >
          {typed}
        </div>

        {showOrgPin && (
          <motion.div
            initial={orgPinAnim.initial}
            animate={orgPinAnim.animate}
            transition={orgPinAnim.transition}
            style={{
              marginTop: 10,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              color: 'rgba(255,255,255,0.85)',
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: '0.5px',
              fontFamily: '"Nexa", "Roboto", "Helvetica", "Arial", sans-serif',
            }}
          >
            <SolarIcon name="map-point" size={18} color={GREEN} />
            <span>{orgDisplayName}</span>
          </motion.div>
        )}

        {showOrgPin && logoSrc && !logoError && (
          <motion.img
            src={logoSrc}
            alt={orgDisplayName}
            initial={orgPinAnim.initial}
            animate={orgPinAnim.animate}
            transition={orgPinAnim.transition}
            style={{
              marginTop: 12,
              maxHeight: 64,
              maxWidth: 180,
              objectFit: 'contain',
              opacity: 0.9,
            }}
          />
        )}
      </motion.div>
    </AnimatePresence>
  );
}
